<script setup>

    import { computed, ref, watch } from 'vue'
    import { storeToRefs } from 'pinia'

    import { deepCopy, handleBlur } from '@/Helpers'
    import { IconStackAdd20Filled } from '@iconify-prerendered/vue-fluent'
    import { XMarkIcon } from '@heroicons/vue/24/solid'
    import { useConfigStore } from '@/Stores'

    const emit = defineEmits(['toggle:criteriaSelectionModal', 'remove:criteria', 'drop:criteria'])

    const configStore = useConfigStore()
    const { criteriaNameDict } = storeToRefs(configStore)

    const isDragOver = ref(false)
    const isDraggable = computed(() => props.type === 'mustHaves' || props.type === 'wowFactors')

    function handleDragStart(event, criteria) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('application/criteria-name-id', criteria.name_id)
        event.dataTransfer.setData('application/criteria-source-type', props.type)
    }

    function handleDragOver(event) {
        if (!isDraggable.value) return
        const sourceType = event.dataTransfer.types.includes('application/criteria-source-type')
        if (!sourceType) return
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
        isDragOver.value = true
    }

    function handleDragLeave(event) {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            isDragOver.value = false
        }
    }

    function handleDrop(event) {
        event.preventDefault()
        isDragOver.value = false
        const nameId = event.dataTransfer.getData('application/criteria-name-id')
        const sourceType = event.dataTransfer.getData('application/criteria-source-type')
        if (nameId && sourceType !== props.type) {
            emit('drop:criteria', nameId)
        }
    }

    const props = defineProps({
        criterias: {
            type: Array,
            default: () => []
        },
        type: {
            type: String,
            required: true
        },
        scrollable: {
            type: Boolean,
            default: false
        },
    })

    const typeStyles = {
        healthConditions: {
            background: 'bg-neutral-100',
            border: 'border border-dashed rounded',
            pillBackground: 'bg-[#dd7a7d]',
            text: 'text-white',
            hover: ''
        },
        mustHaves: {
            background: 'bg-lifeworx-orange-50',
            border: 'border-lifeworx-orange-200 border border-dashed rounded',
            pillBackground: 'bg-lifeworx-orange-100',
            pillBorder: 'border-lifeworx-orange-600',
            text: 'text-lifeworx-orange-600',
            hover: 'hover:opacity-100'
        },
        wowFactors: {
            background: 'bg-lifeworx-purple-50/25',
            border: 'border-lifeworx-purple-100/75 border border-dashed rounded',
            pillBackground: 'bg-lifeworx-purple-100/70',
            pillBorder: 'border-lifeworx-purple-500',
            text: 'text-lifeworx-purple-800',
            hover: 'hover:opacity-100'
        },
        searchMatch: {
            background: '',
            border: '',
            pillBackground: 'bg-lifeworx-purple-100/70',
            pillBorder: 'border-lifeworx-purple-500',
            text: 'text-lifeworx-purple-800',
            hover: ''
        },
        searchMatchMustHave: {
            background: '',
            border: '',
            pillBackground: 'bg-lifeworx-orange-100',
            pillBorder: 'border-lifeworx-orange-600',
            text: 'text-lifeworx-orange-600',
            hover: ''
        },
        searchMissing: {
            background: '',
            border: '',
            pillBackground: 'bg-lifeworx-red-500/5',
            pillBorder: 'border-lifeworx-red-600 border-dashed',
            text: 'text-lifeworx-red-500 italic',
            hover: ''
        }
    }

    const pillStyles = computed(() => {
        if (!props.type || !typeStyles[props.type]) {
            return 'bg-gray-100 text-gray-800 hover:opacity-80 border border-gray-200';
        }
        const styles = typeStyles[props.type]
        return `${styles.pillBackground} ${styles.text} ${styles.hover} border ${styles.pillBorder || ''}`;
    })

    const containerStyles = computed(() => {
        if (!props.type || !typeStyles[props.type]) {
            return 'bg-neutral-100 border-gray-200';
        }
        const styles = typeStyles[props.type]
        return `${styles.background} ${styles.border} ${props.type == 'mustHaves' || props.type == 'wowFactors' ? 'hover:bg-lifeworx-blue-600/10' : ''}`;
    })


    const defaultState = {
        criterias: []
    }

    const state = ref({ ...defaultState })

    watch(() => props.criterias, (newCriterias, oldCriterias) => {
        const newState = {
            criterias: newCriterias?.length > 0 ? deepCopy(newCriterias) : []
        }

        state.value = newState
    }, { immediate: true})

    function getCriteriaPresentationPillText(criteria, withoutExp = false) {
        if (!criteriaNameDict.value[criteria.name_id]) {
            return '';
        }
        let output;
        let presentation = criteriaNameDict.value[criteria.name_id].presentation;
        if (presentation.show === 'label') {
            output = criteriaNameDict.value[criteria.name_id].label
        } else if (presentation.show === 'attributes') {
            output = criteria.attributes
                .filter(attribute => attribute.selected_choice_ids.length > 0)
                .map(attribute => {
                    const selectedChoices = attribute.selected_choice_ids.map(choice_id => {
                        return criteriaNameDict.value[criteria.name_id].attribute_dict[attribute.name_id].choices.find(choice => choice.id === choice_id).label;
                    });
                    return selectedChoices.join(presentation.concat_between);
                }).join(presentation.concat_between);
        } else {
            return criteriaNameDict.value[criteria.name_id].label;
        }
        if (presentation.concat_start) {
            output = presentation.concat_start + ' ' + output;
        }
        if (presentation.concat_end) {
            output = output + ' ' + presentation.concat_end;
        }
        if (withoutExp) {
            let expVariants = [ 'Exp.', 'Experience' ]
            expVariants.forEach((exp) => {
                output = output.replace(exp, '').trim()
            })
        }
        return output;
    }

</script>

<template>

    <div
        :class="[containerStyles, isDragOver && isDraggable ? 'ring-2 ring-lifeworx-blue-600/50 bg-lifeworx-blue-600/5' : '']"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
        @drop="handleDrop"
    >
        <div
            :class="[
                'flex-1 flex flex-col h-full',
                {
                    'min-h-14': props.type !== 'searchMatch' && props.type !== 'searchMatchMustHave' && props.type !== 'searchMissing',
                }
            ]">
            <div
                @click.stop="() => !loading && $emit('toggle:criteriaSelectionModal')"
                :class="[
                    'text-center cursor-pointer h-full',
                    {
                        'items-center justify-center' : props.criterias.length == 0,
                        'items-start justify-start' : props.criterias.length > 0
                    }
                ]"
            >
                <div v-if="props.criterias.length == 0 && (props.type == 'mustHaves' || props.type == 'wowFactors')"
                    class="flex flex-col items-center justify-center py-2 h-full"
                >
                    <IconStackAdd20Filled
                        :class="[
                            'h-8 w-8 mb-1.5',
                            props.type === 'mustHaves' ? 'text-lifeworx-orange-600' :
                            props.type === 'wowFactors' ? 'text-lifeworx-purple-500' :
                            'text-lifeworx-blue-800/30'
                        ]"
                    />
                </div>
                <div v-else-if="props.criterias.length == 0 && props.type == 'healthConditions'"
                    class="flex flex-col items-center py-2"
                >
                    <span class="text-lifeworx-blue-800 font-semibold tracking-wide text-xs">No Health Conditions for Client</span>
                </div>
                <ul
                    v-else
                    :class="[
                        'flex flex-wrap gap-x-2 gap-y-1.5',
                        (props.type === 'healthConditions' || props.type === 'mustHaves' || props.type === 'wowFactors') ? 'p-2' : '',
                        props.scrollable ? 'overflow-y-scroll' : 'overflow-y-auto'
                    ]"
                >
                    <template v-for="criteria in props.criterias" :key="criteria.name_id">
                        <li
                            v-if="getCriteriaPresentationPillText(criteria, true).length > 0"
                            :draggable="isDraggable"
                            @dragstart="(e) => handleDragStart(e, criteria)"
                            @click.stop="() => $emit('toggle:criteriaSelectionModal', criteria.name_id)"
                            :class="[
                                'text-xs inline-flex font-semibold p-1 px-2 rounded-full opacity-90 hover:opacity-100 text-nowrap overflow-ellipsis overflow-hidden',
                                pillStyles,
                                isDraggable ? 'cursor-grab active:cursor-grabbing' : ''
                            ]"
                        >

                            <VTooltip
                                v-if="getCriteriaPresentationPillText(criteria).length > 45"
                                :triggers="['hover']"
                                :autoHide="true"
                                @apply-hide="handleBlur"
                            >
                                <div class="flex items-center gap-2">
                                    <span>{{ getCriteriaPresentationPillText(criteria).slice(0, 50) }}...</span>
                                    <template v-if="props.type == 'mustHaves' || props.type == 'wowFactors'">
                                        <XMarkIcon
                                            :class="[
                                                'h-4 w-4',
                                                typeStyles[props.type]?.text || 'text-gray-400',
                                                'hover:opacity-60'
                                            ]"
                                            @click.stop="() => $emit('remove:criteria', criteria.name_id)"
                                        />
                                    </template>
                                </div>

                                <template #popper>
                                        <span class="text-sm">
                                            {{ getCriteriaPresentationPillText(criteria) }}
                                        </span>
                                </template>

                            </VTooltip>
                            <div v-else class="flex items-center gap-2">
                                    <span>{{ getCriteriaPresentationPillText(criteria) }}</span>
                                    <template v-if="props.type == 'mustHaves' || props.type == 'wowFactors'">
                                        <XMarkIcon
                                            :class="[
                                                'h-4 w-4',
                                                typeStyles[props.type]?.text || 'text-gray-400',
                                                'hover:opacity-60'
                                            ]"
                                            @click.stop="() => $emit('remove:criteria', criteria.name_id)"
                                        />
                                    </template>
                                </div>
                        </li>
                    </template>
                </ul>
            </div>
        </div>
    </div>

</template>
